// frontend/src/pages/Inventory.jsx

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditableGrid } from '../hooks/useEditableGrid';
import { AgGridReact } from "ag-grid-react";
import PageBanner from "../components/common/PageBanner";
import Tile from '../components/layout/Tile';
import BarEnvelopeChart from '../components/charts/BarEnvelopeChart';
import { ItemFilterBar, useGridChartScope, DrillDownChip, drillDownFromCellClick, withDrillDownCursor } from '../features/item-filter';
import { useAuth } from '../contexts/AuthContext';
import { useInventoryTimeline, useInventoryKpi } from '../hooks/useInventoryData';
import { useInAppSyncStatus } from '../hooks/useInAppSyncStatus';
import { catalogImporting } from '../utils/syncGate';
import { inventoryCostState } from '../utils/kpiValueCoverage';
import { useInventoryBulkUpdate } from '../hooks/useInventoryMutations';
import { useAgGridTheme } from '../utils/agGridTheme';
import { AUTO_SIZE_TO_FILL, defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems } from '../utils/gridDefaults';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useFormatters } from '../hooks/useFormatters';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useSsrmGrid, buildExportQuery } from '../hooks/useSsrmGrid';
import { translateRequestError } from '../queries/api';
import { itemLocationRowId } from '../utils/itemLocationRowId';
import { buildPinnedTotalsRow, wrapColumnsForSummaryRows } from '../utils/pinnedTotals';

import ConfirmDialog from '../components/common/ConfirmDialog';
import SaveDiscardButtons from '../components/common/SaveDiscardButtons';
import GridExportButton from '../components/common/GridExportButton';
import BulkImportDialog from '../components/common/BulkImportDialog';
import useBulkImport from '../hooks/useBulkImport';
import { DATA } from '../queries/dataChanged';
import ImportButton from '../components/common/ImportButton';
import { parseWholeUnitsCell } from '../utils/planningCellParsers';
import { safetyStockColDef } from '../utils/safetyStockColDef';
import { minimumStockColDef } from '../utils/minimumStockColDef';
import { dirtyCellPayload } from '../utils/dirtyCellPayload';
import { useIsShopify, productColumn } from '../components/grid/productIdentity';
import { useProductGrouping } from '../components/grid/productGrouping';
import { inTransitColDef } from '../components/grid/inTransitColumn';
import { projectedAvailableColDef } from '../components/grid/projectedAvailableColumn';
import '../styles/pages.css';
import { GridEmptyState } from '../components/grid/NoRowsOverlay';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

// Row fields this grid's drill-down reads (see drillDownFromCellClick).
const DRILL_DOWN_FIELDS = {
  itemCode: 'item_code',
  itemName: 'item_name',
  sku: 'sku',
  locationCode: 'location_code',
  locationName: 'location_name',
};

function Inventory() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const isShopify = useIsShopify();
  const currencySuffix = selectedStore?.currency ? ` (${selectedStore.currency})` : '';
  const { confirm, dialogProps } = useConfirmDialog();
  const columnTypes = useColumnTypes();
  const { formatNumber, formatCompact, formatCurrency, parseNumber } = useFormatters();
  // Figures across the filtered dataset (not the visible page) — the backend
  // returns them alongside `count` and `results` on the first block of each
  // filter epoch. The row count itself is the grid's, via useSsrmGrid.
  // `totals` is the pinned row ({} for an empty set, so no row);
  // `inventoryValue` is the tile's figure (0 for an empty set — an answer).
  const [totals, setTotals] = useState(null);
  const [inventoryValue, setInventoryValue] = useState(null);

  // Page scope: filters (location / group / search) drive both the grid and
  // the timeline chart; drill-down replaces the chart's filter scope when
  // a single row is selected. See useGridChartScope for the contract.
  // Arrow indirection: guardUnsavedChanges is declared below (it needs the
  // editable-grid state) — the scope hook only invokes it, never during render.
  const scope = useGridChartScope({ beforeChange: () => guardUnsavedChanges() });
  const {
    filter: itemFilter,
    selectedLocation, setSelectedLocation,
    selectedProductCategory, setSelectedProductCategory,
    drillDown, drillDownRowId, handleRowSelect, clearDrillDown,
    chartExtraParams,
  } = scope;
  // Arrow indirection on the guard, as for the scope above.
  const grouping = useProductGrouping({
    gridId: 'inventory',
    preferenceKey: 'inventory_grouped',
    leafIdFields: ['item_location_id'],
    beforeToggle: () => guardUnsavedChanges(),
  });
  const { isGrouped, isGroupRow, columns: groupedColumns, variantProductColumn, totalsLabelField } = grouping;

  const gridTheme = useAgGridTheme();

  // Inventory KPI for collapsed sparkline summary
  const { data: invKpiData, error: invKpiError } = useInventoryKpi();
  const bulkUpdateMutation = useInventoryBulkUpdate();


  // The store's first import: the chart and its folded value say "not
  // synchronized" as the Home Inventory tile does.
  const importing = catalogImporting(useInAppSyncStatus());

  const timelineCollapsedItems = useMemo(() => {
    if (!invKpiData) return null;
    const { state } = inventoryCostState({
      totalItems: invKpiData.total_items,
      catalogImporting: importing,
      itemsWithCost: invKpiData.items_with_cost,
      value: invKpiData.latest_value,
      uncostedItems: invKpiData.uncosted_inventory_items,
      discloseGap: invKpiData.partial_cost,
      changePartial: invKpiData.change_partial,
    });
    const value = state === 'notSynchronized' ? t('home.metrics.notSynchronizedShort')
      : state === 'noCost' ? t('home.metrics.noCostShort')
      : formatCompact(invKpiData.latest_value, invKpiData.currency);
    return [{ label: t('home.metrics.inventory'), value }];
  }, [invKpiData, importing, formatCompact, t]);

  // Drill-down: which clicks count and what they carry is the item-filter
  // feature's rule (drillDownFromCellClick); this grid only names its fields.
  const handleDrillDownClick = useCallback((event) => {
    const target = drillDownFromCellClick(event, DRILL_DOWN_FIELDS);
    if (target) handleRowSelect(target);
  }, [handleRowSelect]);
  const gridDefaultColDef = useMemo(() => withDrillDownCursor(
    { ...defaultColDef, ...grouping.defaultColDef },
    DRILL_DOWN_FIELDS,
  ), [grouping.defaultColDef]);

  // The title names the history only while the chart draws some. Tile
  // renders no header at all without a title, so the state starts on a real
  // one rather than on "unknown" until the first payload lands.
  const [timelineHasHistory, setTimelineHasHistory] = useState(true);
  const timelineTitle = t(timelineHasHistory ? 'inventory.timelineTitle' : 'inventory.timelineTitleForecastOnly');

  const gridApiRef = useRef(null);
  const pageSize = 100;

  // Bulk-save scaffolding: dirty cells, save, discard, keyboard-save,
  // and the nav guard are all managed by useEditableGrid.
  const editing = useEditableGrid({
    // Arrow indirection: `refresh` comes from useSsrmGrid below, and this is
    // only ever invoked after a save or discard — never during render.
    refresh: (opts) => refresh(opts),
    getRowId: (row) => row.item_location_id,
    mutation: bulkUpdateMutation,
    // Only the edited cells: the endpoint pushes an on-hand it receives to
    // the channel and reads a safety stock it receives as an override.
    buildPayload: (_rows, changedCells) => dirtyCellPayload(changedCells),
    successKey: 'inventory.messages.saveSuccess',
    failureKey: 'inventory.messages.saveFailed',
  });
  // Destructured separately because cellClassRules is referenced inside
  // createColumnDefs's useCallback — listing the stable callback as a
  // dep keeps the column-defs deps clean (the full `editing` object
  // changes identity every render).
  const { gridContext } = editing;

  // Import — update-only (On hand, Safety stock, Minimum stock) on the shared
  // import rail; On hand routes through the same write path as the grid's bulk
  // save, so the Shopify push happens exactly as if the cell were edited.
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const importHook = useBulkImport({
    modelName: 'inventory',
    endpoint: '/api/inventory/import/',
    changes: [DATA.inventory],
  });

  const createColumnDefs = useCallback(() => {
    const columns = [
      // --- Identifiers ---
      // Shopify grouped view: the product tree (productGrouping), then one
      // Product cell per variant row — variant title and SKU; the product row
      // carries the picture.
      ...groupedColumns([variantProductColumn()]),
      // Shopify flat view: one merchant-facing Product cell (image + name +
      // SKU) replaces the internal item_code / SKU / name trio — the item_code
      // is a Shopify variant-GID suffix that merchants never see in the admin.
      ...(isShopify && !isGrouped ? [
        productColumn({ headerName: t('inventory.columns.product') }),
      ] : []),
      // Non-Shopify (ERP / manual) stores: item_code is a real, merchant-known
      // value — keep the explicit identifier columns.
      ...(!isShopify ? [
        {
          field: "item_code",
          headerName: t("inventory.columns.itemCode"),
        },
        {
          field: "sku",
          headerName: t("inventory.columns.sku"),
          type: 'optionalTextColumn',
        },
        {
          field: "item_name",
          headerName: t("inventory.columns.itemName"),
        },
      ] : []),
      // --- Location / Supplier ---
      {
        field: "location_name",
        headerName: t("inventory.columns.location"),
      },
      {
        field: "supplier_name",
        headerName: t("inventory.columns.supplierName"),
        type: 'optionalTextColumn',
      },
      // --- Quantities --- The four sort in both views: a product row is the
      // sum of its variants, and the server orders the products by it.
      // Everything else stops sorting when the rows are grouped
      // (productGrouping's defaultColDef).
      {
        field: "inventory_on_hand",
        headerName: t("inventory.columns.onHand"),
        editable: (params) => !isGroupRow(params.data),
        type: 'numberColumn',
        sortable: true,
        valueParser: (params) => parseWholeUnitsCell(params, parseNumber),
      },
      {
        field: "inventory_committed",
        headerName: t("inventory.columns.committed"),
        type: 'numberColumn',
        sortable: true,
      },
      // Quantity plus its own explanation — hovering says which supply the
      // number came from. One factory because the cell's value and the
      // tooltip's total have to be the same column (inTransitColumn.js).
      {
        ...inTransitColDef({ headerName: t("inventory.columns.inTransit") }),
        sortable: true,
      },
      // Available + In transit, with every term required — the arithmetic
      // and its null guard live in projectedAvailableColumn.js, which tests
      // them directly.
      {
        ...projectedAvailableColDef({ headerName: t("inventory.columns.projectedAvailable") }),
        sortable: true,
      },
      // --- Financials ---
      {
        field: "cost_price",
        headerName: t("inventory.columns.costPrice") + currencySuffix,
                type: 'decimalColumn',
      },
      {
        field: "sell_price",
        headerName: t("inventory.columns.sellPrice") + currencySuffix,
                type: 'decimalColumn',
      },
      {
        field: "moq",
        headerName: t("inventory.columns.moq"),
                type: 'numberColumn',
      },
      // --- Classifications ---
      {
        field: "abc_class",
        headerName: t("inventory.columns.abc"),
              },
      {
        field: "xyz_class",
        headerName: t("inventory.columns.xyz"),
              },
      // --- Planning parameters ---
      safetyStockColDef({
        t,
        formatNumber,
        parseNumber,
        headerName: t("inventory.columns.safetyStock"),
        editable: (params) => !isGroupRow(params.data),
      }),
      minimumStockColDef({
        t,
        parseNumber,
        editable: (params) => !isGroupRow(params.data),
      }),
      {
        field: "lead_time",
        headerName: t("inventory.columns.leadTime"),
        type: 'numberColumn',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return '-';
          return `${params.value}d`;
        },
      },
    ];

    return columns;
  }, [t, isGrouped, groupedColumns, variantProductColumn, isGroupRow, isShopify, formatNumber, currencySuffix, parseNumber]);

  // Column defs rebuild whenever createColumnDefs's deps change (incl. language via t).
  // Wrapped through wrapColumnsForSummaryRows so columns whose field isn't
  // part of the aggregate payload render empty on the totals and product rows
  // instead of their inline-formatter null fallback (e.g. '-' on lead_time).
  const columnDefs = useMemo(
    () => wrapColumnsForSummaryRows(createColumnDefs()),
    [createColumnDefs],
  );

  // Pinned-bottom totals row — sums on-hand / committed / in-transit / total-stock
  // across the filtered dataset. The label sits in the first column: when flat
  // on item_code (the item_code column on a non-Shopify store; on a Shopify
  // store the Product cell prints the row's code field as plain text on the
  // pinned row, ProductCell.jsx), when grouped in the product tree column
  // (productGrouping's totalsLabelField).
  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, totalsLabelField('item_code'), t('grid.totals.label')),
    [totals, t, totalsLabelField],
  );

  const ssrmParams = useMemo(() => ({
    ...itemFilter.params,
    location_code: selectedLocation === 'all' ? null : selectedLocation,
    product_category_id: selectedProductCategory?.id || null,
  }), [itemFilter.params, selectedLocation, selectedProductCategory]);

  // The filtered export covers exactly the rows the grid lists.
  const exportProps = {
    endpoint: '/api/inventory/export/',
    filename: 'inventory',
    getParams: useCallback(
      (scope) => (scope === 'all' ? '' : buildExportQuery(ssrmParams)),
      [ssrmParams],
    ),
  };

  // Opt in to the full-dataset totals block — this grid is the only consumer
  // (pinned totals row + the "total inventory value" tile subtitle). As an
  // EPOCH param it rides with the row count on the first block after a filter
  // or sort change: the aggregate spans every filtered row rather than the
  // page, so it cannot differ between blocks and must not be re-run per block.
  const ssrmEpochParams = useMemo(() => ({ totals: 1 }), []);

  const handleSsrmData = useCallback((data) => {
    // Only the epoch's first block carries the figures; every later block
    // omits them. Overwriting unconditionally would blank the pinned row and
    // the tile's Inventory Value the moment the merchant paged.
    if (data.totals !== undefined) {
      setTotals(data.totals);
      setInventoryValue(data.total_inventory_value);
    }
  }, []);

  const handleSsrmError = useCallback((err) => {
    showToast(translateRequestError(err, t, 'inventory.messages.failedToLoad'), 'error');
  }, [t]);

  const { onGridReady, onGridPreDestroyed, refresh, pagination } = useSsrmGrid({
    endpoint: '/api/inventory/',
    params: ssrmParams,
    epochParams: ssrmEpochParams,
    pageSize,
    onData: handleSsrmData,
    onError: handleSsrmError,
    gridRef: gridApiRef,
  });

  const guardUnsavedChanges = async () => {
    if (editing.isDirty) {
      const ok = await confirm({
        title: t('common.unsavedChangesTitle'),
        message: t('inventory.messages.unsavedChangesFilter'),
        confirmLabel: t('common.discard'),
        variant: 'warning',
      });
      if (!ok) return false;
      editing.clearAll();
    }
    return true;
  };

  return (
    <div className="page-container">
      {editing.unsavedGuard}
      <PageBanner severity="error" messages={[invKpiError]} />
      {/* Inventory Timeline: historical → today → projected available balance */}
      <div className="tiles-row">
        <Tile
          width="full"
          title={timelineTitle}
          titleState={<DrillDownChip drillDown={drillDown} onClear={clearDrillDown} />}
          subtitle={t('inventory.timelineSubtitle')}
          subtitleAutoHide
          collapsible
          storageKey="inventory-timeline"
          collapsedItems={timelineCollapsedItems}
        >
          <BarEnvelopeChart
            useData={useInventoryTimeline}
            i18nPrefix="inventory.timeline"
            chartId="inventory_timeline"
            controls={{ metric: true }}
            extraParams={chartExtraParams}
            navigator
            onHistoryPresence={setTimelineHasHistory}
            importingReason={importing ? 'inventory_not_synced' : undefined}
          />
        </Tile>
      </div>

      {/* Inventory Grid */}
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.inventory')}
          subtitle={
            // Presence, not truthiness: tracked stock that is all at zero, or
            // no rows at all, has an Inventory Value of exactly 0, and that is
            // an answer, not a missing number. Only stock nobody can price is
            // null (core.utils.priced_total), and that shows the onboarding blurb.
            inventoryValue != null
              ? t("inventory.subtitleWithValue", {
                  value: formatCurrency(inventoryValue, selectedStore?.currency),
                })
              : t("inventory.subtitle")
          }
          // What Inventory Value counts is not guessable from the number, and
          // it deliberately differs from the pinned On hand total beneath it
          // (see InventoryViewSet._compute_figures). The tooltip is the only
          // place that reconciles them, so it is unconditional.
          infoText={t("inventory.valueInfo")}
          // Auto-hide is for the onboarding blurb, not for live money: once
          // there is a value in the subtitle it stays put. Otherwise the
          // 60-day rule folds the subtitle into the info tooltip, where
          // `infoText` wins and the figure would vanish outright.
          subtitleAutoHide={inventoryValue == null}
          editActions={
            <SaveDiscardButtons
              count={editing.changedRows.length}
              saving={editing.isSaving}
              onSave={editing.save}
              onDiscard={editing.discard}
            />
          }
          headerActions={
            <>
              <ImportButton
                entityKey="inventory"
                onClick={async () => {
                  // A bulk update refreshes the grid on success — pending
                  // edits would be silently clobbered without the guard.
                  if (await guardUnsavedChanges()) setImportDialogOpen(true);
                }}
              />
              <GridExportButton {...exportProps} />
            </>
          }
        >
          <GridPage>
            {/* Filters */}
            <ItemFilterBar
              filter={itemFilter}
              productCategoryFilter={{ selected: selectedProductCategory, set: setSelectedProductCategory, show: true }}
              locationFilter={{ selected: selectedLocation, set: setSelectedLocation }}
              actions={grouping.toggle}
            />

            {/* Grid */}
            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  title={t('inventory.empty.title')}
                  message={t('inventory.empty.message')}
                  // External filters (ItemFilterBar) bypass AG Grid, so
                  // the overlay can't infer this on its own.
                  filtersActive={selectedLocation !== 'all' || !!selectedProductCategory || !!itemFilter.filter}
                >
                  <AgGridReact
                    {...defaultGridProps}
                    autoSizeStrategy={AUTO_SIZE_TO_FILL}
                    {...editableGridProps}
                    context={gridContext}
                    theme={gridTheme}
                    {...grouping.gridProps}
                    columnDefs={columnDefs}
                    defaultColDef={gridDefaultColDef}
                    columnTypes={columnTypes}
                    onCellValueChanged={editing.onCellValueChanged}
                    onCellClicked={handleDrillDownClick}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    rowClassRules={{
                      'inventory-row-active': (params) =>
                        drillDownRowId && params.data &&
                        itemLocationRowId(params.data.item_code, params.data.location_code) === drillDownRowId,
                    }}
                    rowModelType="serverSide"
                    {...pagination}
                    pinnedBottomRowData={pinnedBottomRowData}
                    getContextMenuItems={copyOnlyContextMenuItems}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>

      <ConfirmDialog {...dialogProps} />
      <BulkImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        importHook={importHook}
        entityKey="inventory"
      />
    </div>
  );
}

export default Inventory;